import React from 'react';
import { useSelector } from 'react-redux';

import { Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import MobileBottomNavbar from '../../components/MobileBottomNavbar/MobileBottomNavbar';

import heroPlaceholder from '../../assets/stem-toys-hero-placeholder.svg';

import {
  PAGE_META,
  HERO_IMAGE,
  INDUSTRY_OVERVIEW,
  BENEFITS,
  WHY_GREAT_BUSINESS,
  EXCLUSIVE_TERRITORY,
  HOW_TO_GET_STARTED,
  MARKETING,
  TOP_ITEMS,
  WHY_RAS,
  OWNER_PROTECTION,
  MARKET_STATS,
  CLOSING,
  CTA,
  ARTICLE_SCHEMA,
} from './content';

import css from './StartRentalBusinessStemToysPage.module.css';

const Cta = ({ cta }) => (
  <NamedLink
    className={css.primaryCta}
    name={cta.target.name}
    params={cta.target.params}
    aria-label={cta.ariaLabel}
  >
    {cta.text}
  </NamedLink>
);

const Bullet = ({ marker, title, body }) => (
  <li className={css.bullet}>
    <span className={css.bulletMarker} aria-hidden="true">
      {marker}
    </span>
    <div>
      <p className={css.bulletTitle}>{title}</p>
      <p className={css.bulletBody}>{body}</p>
    </div>
  </li>
);

const StartRentalBusinessStemToysPageComponent = () => {
  const currentUser = useSelector(state => state.user?.currentUser);
  const cta = currentUser ? CTA.loggedIn : CTA.loggedOut;

  return (
    <Page
      title={PAGE_META.title}
      description={PAGE_META.description}
      canonicalUrl={PAGE_META.canonicalUrl}
      schema={ARTICLE_SCHEMA}
      scrollingDisabled={false}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mobileBottomNavbar={<MobileBottomNavbar />}
      >
        <main className={css.root}>
          {/* ── Hero ───────────────────────────────────────────────── */}
          <section className={css.heroSection} aria-labelledby="stem-hero-title">
            <div className={css.heroContainer}>
              <div className={css.heroContent}>
                <h1 id="stem-hero-title" className={css.heroTitle}>
                  {PAGE_META.h1}
                </h1>
                <p className={css.heroSubheading}>{PAGE_META.subheading}</p>
                <Cta cta={cta} />
              </div>
              <div className={css.heroImageWrapper}>
                <img
                  className={css.heroImage}
                  src={heroPlaceholder}
                  alt={HERO_IMAGE.alt}
                  width={HERO_IMAGE.width}
                  height={HERO_IMAGE.height}
                  loading="eager"
                  fetchpriority="high"
                />
              </div>
            </div>
          </section>

          {/* ── Industry Overview ──────────────────────────────────── */}
          <section className={css.section} aria-labelledby="stem-industry-heading">
            <div className={css.sectionContainer}>
              <h2 id="stem-industry-heading" className={css.sectionHeading}>
                {INDUSTRY_OVERVIEW.heading}
              </h2>
              <p className={css.sectionBody}>{INDUSTRY_OVERVIEW.body}</p>
            </div>
          </section>

          {/* ── Benefits ────────────────────────────────────────────── */}
          <section
            className={`${css.section} ${css.sectionAlt}`}
            aria-labelledby="stem-benefits-heading"
          >
            <div className={css.sectionContainer}>
              <h2 id="stem-benefits-heading" className={css.sectionHeading}>
                {BENEFITS.heading}
              </h2>
              <ul className={css.bulletList}>
                {BENEFITS.bullets.map((b, i) => (
                  <Bullet key={b.title} marker={i + 1} title={b.title} body={b.body} />
                ))}
              </ul>
            </div>
          </section>

          {/* ── Why Great Business ──────────────────────────────────── */}
          <section
            className={css.section}
            aria-labelledby="stem-why-great-heading"
          >
            <div className={css.sectionContainer}>
              <h2 id="stem-why-great-heading" className={css.sectionHeading}>
                {WHY_GREAT_BUSINESS.heading}
              </h2>
              <p className={css.sectionBody}>{WHY_GREAT_BUSINESS.body}</p>
              <ul className={css.simpleList}>
                {WHY_GREAT_BUSINESS.supportingPoints.map(point => (
                  <li key={point} className={css.simpleListItem}>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* ── Exclusive Territory ─────────────────────────────────── */}
          <section
            className={css.territorySection}
            aria-labelledby="stem-territory-heading"
          >
            <div className={css.territoryContainer}>
              <h2 id="stem-territory-heading" className={css.sectionHeading}>
                {EXCLUSIVE_TERRITORY.heading}
              </h2>
              <p className={css.sectionBody}>{EXCLUSIVE_TERRITORY.body}</p>
              <span className={css.territoryUrgency}>
                {EXCLUSIVE_TERRITORY.urgency}
              </span>
            </div>
          </section>

          {/* ── How to Get Started ──────────────────────────────────── */}
          <section
            className={`${css.section} ${css.sectionAlt}`}
            aria-labelledby="stem-steps-heading"
          >
            <div className={css.sectionContainer}>
              <h2 id="stem-steps-heading" className={css.sectionHeading}>
                {HOW_TO_GET_STARTED.heading}
              </h2>
              <ol className={css.stepsList}>
                {HOW_TO_GET_STARTED.steps.map(step => (
                  <li key={step.title} className={css.step}>
                    <span className={css.stepNumber} aria-hidden="true" />
                    <div>
                      <h3 className={css.stepTitle}>{step.title}</h3>
                      <p className={css.stepBody}>{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>

          {/* ── Marketing ───────────────────────────────────────────── */}
          <section className={css.section} aria-labelledby="stem-marketing-heading">
            <div className={css.sectionContainer}>
              <h2 id="stem-marketing-heading" className={css.sectionHeading}>
                {MARKETING.heading}
              </h2>
              <ul className={css.simpleList}>
                {MARKETING.bullets.map(bullet => (
                  <li key={bullet} className={css.simpleListItem}>
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
          </section>

          {/* ── Top 20 Items ────────────────────────────────────────── */}
          <section
            className={`${css.section} ${css.sectionAlt}`}
            aria-labelledby="stem-top-items-heading"
          >
            <div className={css.sectionContainer}>
              <h2 id="stem-top-items-heading" className={css.sectionHeading}>
                {TOP_ITEMS.heading}
              </h2>
              <ol className={css.topItemsList}>
                {TOP_ITEMS.items.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ol>
            </div>
          </section>

          {/* ── Why RAS ─────────────────────────────────────────────── */}
          <section className={css.section} aria-labelledby="stem-why-ras-heading">
            <div className={css.sectionContainer}>
              <h2 id="stem-why-ras-heading" className={css.sectionHeading}>
                {WHY_RAS.heading}
              </h2>
              <ul className={css.bulletList}>
                {WHY_RAS.bullets.map((bullet, i) => (
                  <Bullet
                    key={bullet}
                    marker={i + 1}
                    title={bullet}
                    body=""
                  />
                ))}
              </ul>
            </div>
          </section>

          {/* ── Owner Protection callout ────────────────────────────── */}
          <section
            className={css.ownerProtectionSection}
            aria-labelledby="stem-owner-protection-heading"
          >
            <div className={css.ownerProtectionCard}>
              <span className={css.ownerProtectionBadge}>
                {OWNER_PROTECTION.heading}
              </span>
              <div>
                <p className={css.ownerProtectionBody}>{OWNER_PROTECTION.body}</p>
                <NamedLink
                  className={css.ownerProtectionLink}
                  name={OWNER_PROTECTION.linkTarget.name}
                  params={OWNER_PROTECTION.linkTarget.params}
                >
                  {OWNER_PROTECTION.linkText}
                </NamedLink>
              </div>
            </div>
          </section>

          {/* ── Market Stats ────────────────────────────────────────── */}
          <section
            className={`${css.section} ${css.sectionAlt}`}
            aria-labelledby="stem-stats-heading"
          >
            <div className={css.sectionContainer}>
              <h2 id="stem-stats-heading" className={css.sectionHeading}>
                {MARKET_STATS.heading}
              </h2>
              <div className={css.statsGrid}>
                {MARKET_STATS.stats.map(stat => (
                  <div key={stat.label} className={css.statCard}>
                    <span className={css.statValue}>{stat.value}</span>
                    <span className={css.statLabel}>{stat.label}</span>
                    <p className={css.statBody}>{stat.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ── Closing CTA ─────────────────────────────────────────── */}
          <section
            className={css.closingSection}
            aria-labelledby="stem-closing-heading"
          >
            <div className={css.closingContainer}>
              <h2 id="stem-closing-heading" className={css.closingHeading}>
                {CLOSING.heading}
              </h2>
              <p className={css.closingBody}>{CLOSING.body}</p>
              <Cta cta={cta} />
            </div>
          </section>
        </main>
      </LayoutSingleColumn>
    </Page>
  );
};

export default StartRentalBusinessStemToysPageComponent;
